import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Car, Wrench, Loader2, Send, CheckCircle2, ShieldAlert } from "lucide-react";

// Signalement véhicule (module optionnel) : véhicule → type → champs adaptés → commentaire → envoi. Pas de kilométrage ni GPS.
export default function ReportVehicle() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState(null);
  const [types, setTypes] = useState([]);
  const [vehicle, setVehicle] = useState("");
  const [type, setType] = useState("");
  const [details, setDetails] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [eqEnabled, setEqEnabled] = useState(false);
  useEffect(() => {
    api.get("/appointments/module-status").then((r) => { if (!r.data.vehicle_reports_enabled) nav("/driver", { replace: true }); setEqEnabled(!!r.data.vehicle_equipment_enabled); }).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
    api.get("/vehicle-reports/types").then((r) => setTypes(r.data.types)).catch(() => {});
  }, [nav]);
  const t = types.find((x) => x.value === type);
  const submit = async () => {
    if (!vehicle || !type) { toast.error("Choisissez le véhicule et le type de problème"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/vehicle-reports", { vehicle_id: vehicle, type, details, comment });
      toast.success(data.email?.status === "sent" ? "Signalement enregistré et envoyé ✅" : "Signalement enregistré (email non envoyé : la régulation le verra dans SanitDocs)");
      nav("/driver", { replace: true });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="space-y-5 pb-28" data-testid="report-vehicle-page">
      <div className="flex items-center justify-between gap-3 flex-wrap"><div><p className="text-xs font-semibold tracking-widest uppercase text-accent-700">Véhicule</p><h1 className="text-2xl font-heading font-extrabold text-brand-900 mt-0.5">Signaler un problème</h1></div>
        {eqEnabled && <button type="button" data-testid="report-to-equipment" onClick={() => nav("/driver/equipements")} className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border-2 border-slate-300 bg-white text-sm font-bold text-brand-900"><ShieldAlert className="w-4 h-4 text-accent-600" />Échéances véhicules</button>}</div>
      <section>
        <Label className="font-bold">1. Véhicule</Label>
        {vehicles && vehicles.length === 0 && <p className="text-sm text-slate-500 mt-1">Aucun véhicule enregistré : demandez à votre administrateur.</p>}
        <div className="grid grid-cols-1 gap-2 mt-2">
          {(vehicles || []).map((v) => (
            <button key={v.id} type="button" data-testid={`vehicle-${v.id}`} onClick={() => setVehicle(v.id)} className={`flex items-center gap-3 text-left rounded-2xl border-2 p-4 bg-white ${vehicle === v.id ? "border-accent-500" : "border-slate-200"}`}>
              <Car className="w-6 h-6 text-slate-400" /><div><p className="font-heading font-bold text-brand-900">{v.plate}</p><p className="text-sm text-slate-500">{v.brand} {v.model}</p></div>
              {vehicle === v.id && <CheckCircle2 className="w-5 h-5 text-accent-600 ml-auto" />}
            </button>
          ))}
        </div>
      </section>
      <section>
        <Label className="font-bold">2. Type de signalement</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {types.map((x) => (
            <button key={x.value} type="button" data-testid={`type-${x.value}`} onClick={() => { setType(x.value); setDetails({}); }} className={`rounded-2xl border-2 p-3 text-sm font-bold text-left bg-white ${type === x.value ? "border-accent-500 text-accent-800" : "border-slate-200 text-slate-700"}`}>{x.label}</button>
          ))}
        </div>
      </section>
      {t && (
        <section className="space-y-3" data-testid="type-fields">
          {t.fields.map((f) => (
            <div key={f.key}>
              {f.kind === "bool" ? (
                <label className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-3"><input type="checkbox" data-testid={`field-${f.key}`} checked={!!details[f.key]} onChange={(e) => setDetails({ ...details, [f.key]: e.target.checked })} className="w-5 h-5" /><span className="font-semibold text-slate-700">{f.label}</span></label>
              ) : f.kind === "select" ? (
                <><Label>{f.label}</Label><select data-testid={`field-${f.key}`} value={details[f.key] || ""} onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })} className="mt-1 h-12 w-full rounded-xl border border-slate-200 px-3 bg-white"><option value="">—</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select></>
              ) : (
                <><Label>{f.label}</Label><Input data-testid={`field-${f.key}`} value={details[f.key] || ""} onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })} className="mt-1 h-12" /></>
              )}
            </div>
          ))}
          <div><Label>Commentaire</Label><Textarea data-testid="report-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="mt-1" placeholder="Précisions utiles…" /></div>
        </section>
      )}
      <div className="fixed bottom-4 inset-x-0 mx-auto max-w-md px-4">
        <button data-testid="submit-vehicle-report" disabled={busy || !vehicle || !type} onClick={submit} className="w-full h-16 rounded-2xl bg-accent-600 text-white text-lg font-bold shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98]">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}Envoyer le signalement
        </button>
      </div>
    </div>
  );
}
